/* screens/academy/landing/LandingEditor.jsx — редактор лендинга курса (#16, авторская сторона).
   Методист включает/выключает лендинг и собирает его из СТРУКТУРНЫХ блоков (типы и поля —
   из BLOCK_SCHEMA в blocks.jsx, который точно повторяет landing-blocks.json → сохранённый
   landing_json проходит бэк-валидацию, S13). Сохранение — updateCourse(id, {landing_json}).
   «Предпросмотр» открывает /academy/course/:id/preview в новой вкладке (рендерит ЧЕРНОВИК).

   БЕЗОПАСНОСТЬ: формы лишь редактируют поля schema → в landing_json не попадают чужие
   ключи; рендер/превью санитайзят сами (S5/S6). dangerouslySetInnerHTML здесь нет.
   Картинки блоков загружаются файлом в наш публичный медиа-контур (AD-7) либо задаются
   ссылкой; вид адреса бэк жёстко требует ^https?://, валидацию держит он же. */

import React from 'react';
import { Button } from '../../../components/ui/Button.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { Field, TextInput, Textarea, NativeSelect, Checkbox } from '../../../components/ui/Field.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import * as api from '../../../lib/api/academy.js';
import { BLOCK_TYPES, BLOCK_SCHEMA, blockTypeLabel, emptyBlock, safeImg,
  FORM_FIELD_TYPE_OPTIONS, FORM_FIELD_ORDER } from './blocks.jsx';
import { genFieldKey, deepCopyBlock, moveInList, normMeta } from './editorLogic.js';

const { useState, useRef, useCallback } = React;

// Стабильный id нового блока/строки: crypto.randomUUID, иначе счётчик-фолбэк.
let _idSeq = 0;
function genId(prefix) {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* нет crypto — падаем на счётчик */ }
  _idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idSeq}`;
}

// genFieldKey / deepCopyBlock / moveInList / normMeta живут в ./editorLogic.js —
// чистая логика, у неё свой тест без React.

// ── картинка блока: загрузка файла + то же поле адреса (AD-7) ────────────────
// Раньше здесь было голое текстовое поле URL: чтобы поставить фото автора или
// обложку блока, методисту приходилось искать внешний хостинг. Загруженный файл
// уезжает в наш публичный медиа-контур, а его абсолютный адрес подставляется в
// то же поле — ручной ввод ссылки остаётся рабочим (бэк по-прежнему требует
// http(s)://, и наш адрес этому виду соответствует).
function ImageField({ desc, value, onChange, courseId }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = safeImg(value);

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Сбрасываем input сразу: иначе после ошибки тот же файл повторно не выбрать.
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadLandingImage(courseId, file);
      onChange((res && res.url) || '');
    } catch (err) {
      toast.show(err?.detail || err?.message || 'Не удалось загрузить изображение', 'danger');
    } finally { setBusy(false); }
  };

  return (
    <Field label={desc.label} hint={desc.hint}>
      <div className="land-image">
        <div className="land-image__preview" aria-hidden="true">
          {preview
            ? <img src={preview} alt="" />
            : <Icon name="image" size={20} />}
        </div>
        <div className="land-image__body">
          <TextInput value={value ?? ''} onChange={(e) => onChange(e.target.value)}
            placeholder="https://…" />
          <div className="land-image__actions">
            <Button size="sm" variant="secondary" iconLeft="upload" loading={busy}
              onClick={() => fileRef.current && fileRef.current.click()}>Загрузить файл</Button>
            {value ? (
              <Button size="sm" variant="ghost" iconLeft="trash"
                onClick={() => onChange('')}>Убрать</Button>
            ) : null}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
        </div>
      </div>
    </Field>
  );
}

// ── AD-14: воронка CRM для заявок формы ──────────────────────────────────────
// Список воронок приезжает в карточке курса (`crm_pipelines`, гейт methodist) —
// /api/crm/pipelines/ методисту недоступен, оттуда список был бы пуст.
function PipelineField({ desc, value, onChange, pipelines }) {
  const list = Array.isArray(pipelines) ? pipelines : [];
  const options = [{ value: '', label: 'Воронка по умолчанию' },
    ...list.map((p) => ({ value: p.slug, label: p.name + (p.is_default ? ' (по умолчанию)' : '') }))];
  // Сохранённая воронка исчезла из списка (архивна/переименован код) — показываем
  // её отдельной строкой, иначе select молча подменил бы выбор методиста первым
  // вариантом, а заявки продолжали бы уходить туда же.
  const known = list.some((p) => p.slug === value);
  if (value && !known) options.push({ value, label: `${value} — воронка не найдена` });
  return (
    <Field label={desc.label} hint={desc.hint}>
      <NativeSelect value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        options={options} />
      {value && !known && (
        <span className="t-caption text-mute">
          Такой воронки в CRM нет — заявки уйдут в воронку по умолчанию.
        </span>
      )}
    </Field>
  );
}

// ── AD-14: конструктор ДОП. полей формы заявки ───────────────────────────────
// Базовые ФИО/телефон/email/комментарий здесь не перечислены — они есть всегда.
function FormFieldsField({ desc, rows, onChange }) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  const setRow = (i, patch) => onChange(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...list, { key: genFieldKey(), label: '', type: 'text' }]);
  const delRow = (i) => onChange(list.filter((_, j) => j !== i));
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  // Варианты списка методист вводит строкой через запятую — отдельный конструктор
  // вариантов внутри конструктора полей на 380 px нечитаем.
  const setOptions = (i, raw) => setRow(i, {
    options: raw.split(',').map((s) => s.trim()).filter(Boolean),
  });
  return (
    <div className="acad-section">
      <div className="acad-section__head">
        <Icon name="file-text" size={15} />
        <span className="acad-section__title">{desc.label}</span>
      </div>
      {desc.hint && <span className="t-caption text-mute">{desc.hint}</span>}
      {list.length === 0 && (
        <span className="t-caption text-mute">
          Своих полей нет — форма спросит ФИО, телефон, email и комментарий.
        </span>
      )}
      {list.map((row, i) => {
        // Ключ выдаётся генератором и не редактируется, поэтому совпасть с базовым
        // может только поле из лендинга, собранного руками до AD-14. Такое поле
        // форма не покажет (базовые поля она спрашивает сама) — говорим об этом
        // прямо, иначе методист правил бы подпись и не понимал, почему поля нет.
        const clash = FORM_FIELD_ORDER.includes((row.key || '').trim());
        return (
          <div key={row.key || i} className="land-item">
            <div className="land-item__head">
              <span className="t-caption text-mute num">#{i + 1}</span>
              <span className="acad-section__spacer" />
              <Button size="sm" variant="ghost" iconLeft="chevron-up" disabled={i === 0}
                onClick={() => moveRow(i, -1)} aria-label="Выше" title="Выше" />
              <Button size="sm" variant="ghost" iconLeft="chevron-down" disabled={i === list.length - 1}
                onClick={() => moveRow(i, 1)} aria-label="Ниже" title="Ниже" />
              <Button size="sm" variant="ghost" iconLeft="trash" onClick={() => delRow(i)}
                aria-label="Удалить поле" title="Удалить поле" />
            </div>
            {/* Название обязательно: без него посетитель страницы продаж видел
                над полем технический ключ вида «f_zl61745f1f». Ошибку показываем
                у самого поля, а не тостом при сохранении — иначе методист жмёт
                «Сохранить» и ищет, что не так. Бэк валидирует это же (лендинг
                правят и через API). */}
            <Field label="Название поля"
              error={!String(row.label || '').trim()
                ? 'Без названия посетитель увидит технический ключ поля' : undefined}
              hint={clash
                ? `Ключ «${row.key}» совпадает с базовым полем — форма это поле не покажет; удалите его и добавьте заново`
                : undefined}>
              <TextInput value={row.label ?? ''} placeholder="Например: Должность"
                onChange={(e) => setRow(i, { label: e.target.value })} />
            </Field>
            <Field label="Тип">
              <NativeSelect value={row.type || 'text'} options={FORM_FIELD_TYPE_OPTIONS}
                onChange={(e) => setRow(i, { type: e.target.value })} />
            </Field>
            {(row.type || 'text') === 'select' && (
              <Field label="Варианты" hint="через запятую">
                <TextInput value={(row.options || []).join(', ')}
                  onChange={(e) => setOptions(i, e.target.value)} />
              </Field>
            )}
          </div>
        );
      })}
      <div>
        <Button size="sm" variant="ghost" iconLeft="plus" onClick={addRow}>Добавить поле</Button>
      </div>
    </div>
  );
}

// ── поле-скаляр одного дескриптора ───────────────────────────────────────────
function ScalarField({ desc, value, onChange, courseId, pipelines }) {
  const v = value ?? '';
  if (desc.kind === 'image') {
    return <ImageField desc={desc} value={value} onChange={onChange} courseId={courseId} />;
  }
  if (desc.kind === 'pipeline') {
    return <PipelineField desc={desc} value={v} onChange={onChange} pipelines={pipelines} />;
  }
  if (desc.kind === 'textarea') {
    return (
      <Field label={desc.label} hint={desc.hint}>
        <Textarea value={v} minRows={3} onChange={(e) => onChange(e.target.value)} />
      </Field>
    );
  }
  if (desc.kind === 'select') {
    return (
      <Field label={desc.label} hint={desc.hint}>
        <NativeSelect value={v || (desc.options[0] && desc.options[0].value) || ''}
          onChange={(e) => onChange(e.target.value)} options={desc.options} />
      </Field>
    );
  }
  // text → одна строка
  return (
    <Field label={desc.label} hint={desc.hint}>
      <TextInput value={v} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

// ── редактор массива items[] (program/features/reviews/faq) ──────────────────
function ItemsField({ desc, rows, onChange, courseId }) {
  const list = Array.isArray(rows) ? rows : [];
  const setRow = (i, key, val) => onChange(list.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const addRow = () => onChange([...list, {}]);
  const delRow = (i) => onChange(list.filter((_, j) => j !== i));
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="acad-section">
      <div className="acad-section__head">
        <Icon name="grid" size={15} /><span className="acad-section__title">{desc.label}</span>
      </div>
      {list.length === 0 && <span className="t-caption text-mute">Пока пусто — добавьте строку.</span>}
      {list.map((row, i) => (
        <div key={i} className="land-item">
          <div className="land-item__head">
            <span className="t-caption text-mute num">#{i + 1}</span>
            <span className="acad-section__spacer" />
            <Button size="sm" variant="ghost" iconLeft="chevron-up" disabled={i === 0}
              onClick={() => moveRow(i, -1)} aria-label="Выше" title="Выше" />
            <Button size="sm" variant="ghost" iconLeft="chevron-down" disabled={i === list.length - 1}
              onClick={() => moveRow(i, 1)} aria-label="Ниже" title="Ниже" />
            <Button size="sm" variant="ghost" iconLeft="trash"
              onClick={() => delRow(i)} aria-label="Удалить строку" title="Удалить строку" />
          </div>
          {desc.item.fields.map((f) => (
            <ScalarField key={f.key} desc={f} value={row[f.key]} courseId={courseId}
              onChange={(val) => setRow(i, f.key, val)} />
          ))}
        </div>
      ))}
      <div><Button size="sm" variant="ghost" iconLeft="plus" onClick={addRow}>Добавить строку</Button></div>
    </div>
  );
}

// Чтение/запись поля блока с поддержкой вложенного ключа «a.b» (напр. form.submit_label).
// Только один уровень вложенности — этого хватает для cta.form.{submit_label,success_text}.
function readField(obj, key) {
  if (key.indexOf('.') === -1) return obj[key];
  const [a, b] = key.split('.');
  return obj[a] && typeof obj[a] === 'object' ? obj[a][b] : undefined;
}
function writeField(obj, key, val) {
  if (key.indexOf('.') === -1) return { ...obj, [key]: val };
  const [a, b] = key.split('.');
  const nested = (obj[a] && typeof obj[a] === 'object') ? obj[a] : {};
  return { ...obj, [a]: { ...nested, [b]: val } };
}

// ── один блок (заголовок-тип + порядок/дубль/удаление + форма полей типа) ─────
// AD-16: порядок меняется ТРЕМЯ способами, и все три обязательны:
//   · мышь — HTML5-DnD за рукоятку (тот же механизм, что в CRM-доске и строке
//     блюда меню: draggable + dataTransfer, без новых зависимостей);
//   · клавиатура — стрелки ↑/↓ на самой рукоятке (она <button>, попадает в Tab);
//   · кнопки «Поднять/Опустить» — остаются как явная, всегда видимая мера.
// Перетаскивание доступно только за рукоятку: карточка набита полями ввода, и
// draggable на всей карточке ломал бы выделение текста мышью.
function BlockCard({ block, index, total, courseId, pipelines, onChange, onMove, onDelete,
  onDuplicate, drag }) {
  const schema = BLOCK_SCHEMA[block.type];
  const set = (key, val) => onChange(writeField(block, key, val));
  const gripKey = (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); onMove(-1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); onMove(1); }
  };
  return (
    <div className={'acad-module land-block'
        + (drag && drag.dragging ? ' is-dragging' : '')
        + (drag && drag.isOver ? ' is-over' : '')}
      onDragOver={drag ? drag.onDragOver : undefined}
      onDrop={drag ? drag.onDrop : undefined}
      onDragLeave={drag ? drag.onDragLeave : undefined}>
      <div className="acad-module__head">
        <button type="button" className="acad-grip land-block__grip" draggable
          onDragStart={drag ? drag.onDragStart : undefined}
          onDragEnd={drag ? drag.onDragEnd : undefined}
          onKeyDown={gripKey}
          aria-label={`Переместить блок «${blockTypeLabel(block.type)}»: стрелки вверх и вниз`}
          title="Перетащите или используйте стрелки ↑ ↓">
          <Icon name="grip" size={16} />
        </button>
        <span className="land-block__type">{blockTypeLabel(block.type)}</span>
        <span className="acad-section__spacer" />
        {/* Четыре кнопки в одной группе, которая не рвётся: на 380 шапка
            переносилась так, что «Поднять» оставалось на первой строке, а
            «Опустить» уезжало на вторую — пара стрелок разъезжалась, и кнопки
            прыгали от блока к блоку. */}
        <span className="land-block__acts">
          <Button size="sm" variant="ghost" iconLeft="chevron-up" disabled={index === 0}
            onClick={() => onMove(-1)} aria-label="Поднять блок" title="Поднять блок" />
          <Button size="sm" variant="ghost" iconLeft="chevron-down" disabled={index === total - 1}
            onClick={() => onMove(1)} aria-label="Опустить блок" title="Опустить блок" />
          <Button size="sm" variant="ghost" iconLeft="copy"
            onClick={onDuplicate} aria-label="Дублировать блок" title="Дублировать блок" />
          <Button size="sm" variant="ghost" iconLeft="trash"
            onClick={onDelete} aria-label="Удалить блок" title="Удалить блок" />
        </span>
      </div>
      <div className="acad-module__body">
        {!schema && <span className="t-caption text-mute">Неизвестный тип «{block.type}» — удалите блок.</span>}
        {schema && schema.fields.map((f) => {
          if (f.showWhen && !f.showWhen(block)) return null;
          if (f.kind === 'items') {
            return <ItemsField key={f.key} desc={f} rows={readField(block, f.key)} courseId={courseId}
              onChange={(rows) => set(f.key, rows)} />;
          }
          if (f.kind === 'formfields') {
            return <FormFieldsField key={f.key} desc={f} rows={readField(block, f.key)}
              onChange={(rows) => set(f.key, rows)} />;
          }
          return <ScalarField key={f.key} desc={f} value={readField(block, f.key)} courseId={courseId}
            pipelines={pipelines} onChange={(val) => set(f.key, val)} />;
        })}
      </div>
    </div>
  );
}

// ── «Добавить блок»: выбор типа → append ─────────────────────────────────────
function AddBlock({ onAdd }) {
  const [type, setType] = useState(BLOCK_TYPES[0]);
  const options = BLOCK_TYPES.map((t) => ({ value: t, label: blockTypeLabel(t) }));
  return (
    <div className="cluster cluster-8" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Field label="Тип блока" className="grow">
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} options={options} />
      </Field>
      <Button variant="secondary" iconLeft="plus" onClick={() => onAdd(type)}>Добавить блок</Button>
    </div>
  );
}

function LandingTab({ course, reload }) {
  const toast = useToast();
  const initial = (course.landing_json && typeof course.landing_json === 'object')
    ? course.landing_json : {};
  // нормализуем форму: дефолт {enabled:false, blocks:[]}; каждому блоку гарантируем id.
  // meta приходит вторым аргументом (свой ключ карточки курса) — если её не собрать
  // в state, сохранение лендинга затирало бы SEO-поля на каждом «Сохранить».
  const norm = useCallback((src, meta) => {
    const blocks = Array.isArray(src.blocks) ? src.blocks : [];
    return {
      enabled: !!src.enabled,
      blocks: blocks
        .filter((b) => b && typeof b === 'object')
        .map((b) => ({ ...b, id: b.id || genId('blk') })),
      meta: normMeta(meta !== undefined ? meta : src.meta),
    };
  }, []);
  const [state, setState] = useState(() => norm(initial, course.landing_meta));
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // ошибка публикации (publish-gate): бэк возвращает 400 c полем status, когда лендинг
  // не включён/пуст. Показываем её рядом с кнопкой публикации, НЕ глотая (scar S13).
  const [publishError, setPublishError] = useState('');
  // снимок исходного для индикации несохранённых изменений
  const baseline = useRef(JSON.stringify(norm(initial, course.landing_meta)));
  const dirty = JSON.stringify(state) !== baseline.current;
  const isPublished = course.status === 'published';
  // AD-16: индекс перетаскиваемого блока и блока-цели (подсветка места вставки).
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const setEnabled = (v) => setState((s) => ({ ...s, enabled: v }));
  const setMeta = (key, val) => setState((s) => ({ ...s, meta: { ...s.meta, [key]: val } }));
  const setBlock = (i, next) => setState((s) => ({ ...s, blocks: s.blocks.map((b, j) => (j === i ? next : b)) }));
  const moveBlock = (i, dir) => setState((s) => {
    const j = i + dir;
    if (j < 0 || j >= s.blocks.length) return s;
    const blocks = s.blocks.slice();
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    return { ...s, blocks };
  });
  // Перенос блока на произвольную позицию (drop) — общий чистый moveInList.
  const moveBlockTo = (from, to) => setState((s) => {
    const blocks = moveInList(s.blocks, from, to);
    return blocks === s.blocks ? s : { ...s, blocks };
  });
  const delBlock = (i) => setState((s) => ({ ...s, blocks: s.blocks.filter((_, j) => j !== i) }));
  // AD-16: «Дублировать» — глубокая копия рядом с оригиналом (свой id, свой items[]).
  const dupBlock = (i) => setState((s) => {
    const src = s.blocks[i];
    if (!src) return s;
    const copy = { ...deepCopyBlock(src), id: genId('blk') };
    const blocks = s.blocks.slice();
    blocks.splice(i + 1, 0, copy);
    return { ...s, blocks };
  });
  const addBlock = (type) => setState((s) => ({
    ...s, blocks: [...s.blocks, { ...emptyBlock(type), id: genId('blk') }],
  }));

  // Обработчики DnD одного блока (мышь). Клавиатурный путь — стрелки на рукоятке.
  const dragProps = (i) => ({
    dragging: dragIdx === i,
    isOver: overIdx === i && dragIdx !== null && dragIdx !== i,
    onDragStart: (e) => {
      setDragIdx(i);
      try {
        e.dataTransfer.effectAllowed = 'move';
        // Тело нужно, иначе Firefox не начинает перетаскивание.
        e.dataTransfer.setData('text/plain', String(i));
      } catch { /* noop */ }
    },
    onDragOver: (e) => {
      if (dragIdx === null) return;   // тащат что-то чужое — не перехватываем
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'move'; } catch { /* noop */ }
      if (overIdx !== i) setOverIdx(i);
    },
    onDragLeave: () => setOverIdx((cur) => (cur === i ? null : cur)),
    onDrop: (e) => {
      e.preventDefault();
      const from = dragIdx;
      setDragIdx(null); setOverIdx(null);
      moveBlockTo(from, i);
    },
    onDragEnd: () => { setDragIdx(null); setOverIdx(null); },
  });

  const save = useCallback(async () => {
    setBusy(true);
    try {
      const saved = await api.updateCourse(course.id, { landing_json: state });
      // фиксируем новый baseline из сохранённого (бэк может нормализовать)
      const back = (saved && saved.landing_json && typeof saved.landing_json === 'object')
        ? norm(saved.landing_json, saved.landing_meta) : state;
      baseline.current = JSON.stringify(back);
      setState(back);
      toast.show('Лендинг сохранён', 'success');
      reload();
      return true;
    } catch (e) {
      // бэк-валидация (плохой URL, неизвестный тип, превышение лимитов) → конкретное сообщение
      toast.show(e?.detail || e?.message || 'Не удалось сохранить лендинг', 'danger');
      return false;
    } finally { setBusy(false); }
  }, [course.id, state, norm, toast, reload]);

  // Предпросмотр читает сохранённый черновик с сервера → если есть несохранённые
  // правки, сначала сохраняем (иначе превью покажет прошлое состояние). При ошибке
  // валидации не открываем вкладку со старым черновиком.
  const openPreview = useCallback(async () => {
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    window.open('/academy/course/' + course.id + '/preview', '_blank', 'noopener');
  }, [course.id, dirty, save]);

  // Публикация курса. Лендинг ОБЯЗАТЕЛЕН: бэк отклоняет publish с 400 на поле `status`,
  // если лендинг выключен/пуст (publish-gate). Сначала сохраняем несохранённые правки
  // лендинга (иначе бэк проверит прошлое состояние), потом шлём status=published.
  // Ошибку гейта показываем дословно рядом с кнопкой (scar S13: не глотать field-error).
  const publish = useCallback(async () => {
    setPublishError('');
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    setPublishing(true);
    try {
      await api.updateCourse(course.id, { status: 'published' });
      toast.show('Курс опубликован', 'success');
      reload();
    } catch (e) {
      // 400 publish-gate приходит как {status:["…"]} → unwrap кладёт текст в e.message
      // и e.field='status' (detail при field-error отсутствует). Показываем у кнопки.
      const msg = (e && e.field === 'status' && e.message)
        || e?.detail || e?.message || 'Не удалось опубликовать курс.';
      setPublishError(msg);
    } finally { setPublishing(false); }
  }, [course.id, dirty, save, toast, reload]);

  return (
    <div className="stack stack-6">
      <section className="acad-section">
        <div className="cluster cluster-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Checkbox label="Лендинг включён" checked={state.enabled}
            onChange={(e) => setEnabled(e.target.checked)} />
          <div className="cluster cluster-8">
            <Button variant="ghost" iconLeft="external-link" onClick={openPreview}>Предпросмотр</Button>
            <Button variant="primary" iconLeft="check" loading={busy} disabled={!dirty} onClick={save}>
              Сохранить{dirty ? ' •' : ''}
            </Button>
          </div>
        </div>
        {!state.enabled && (
          <div className="land-note">
            <Icon name="info" size={15} />
            <span className="t-caption">
              Лендинг выключен — посетители увидят авто-лендинг по умолчанию
              (из названия, краткого описания, описания, программы и тарифов курса).
              Включите тумблер, чтобы показывать собранный из блоков ниже.
            </span>
          </div>
        )}
        <span className="t-caption text-mute">
          Тексты блоков поддерживают Markdown. Картинки можно загрузить файлом или
          указать ссылкой http(s)://. «Предпросмотр» сохраняет изменения и открывает
          страницу курса в новой вкладке.
        </span>

        {/* Публикация курса. Лендинг ОБЯЗАТЕЛЕН — иначе бэк отклонит публикацию
            (publish-gate, 400 на поле status). Ошибку показываем дословно ниже. */}
        <div className="land-publish cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {isPublished ? (
            <span className="t-caption" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <Icon name="check-circle" size={15} />Курс опубликован
            </span>
          ) : (
            <>
              <Button variant="primary" iconLeft="globe" loading={publishing} onClick={publish}>
                Опубликовать курс
              </Button>
              <span className="t-caption text-mute">
                Для публикации лендинг должен быть включён и содержать хотя бы заголовок/hero.
              </span>
            </>
          )}
        </div>
        {publishError && (
          <div className="land-note land-note--error" role="alert">
            <Icon name="alert-circle" size={15} />
            <span className="t-caption">{publishError}</span>
          </div>
        )}
      </section>

      <section className="acad-section">
        <div className="acad-section__head">
          <Icon name="layers" size={15} /><span className="acad-section__title">Блоки</span>
        </div>
        {state.blocks.length === 0 ? (
          <div className="acad-block-empty">
            <Icon name="layers" size={28} />
            <div className="acad-block-empty__title">Блоков пока нет</div>
            <span className="t-caption">Добавьте блок, чтобы собрать лендинг курса.</span>
          </div>
        ) : (
          <div className="stack stack-4">
            {state.blocks.map((b, i) => (
              <BlockCard key={b.id} block={b} index={i} total={state.blocks.length} courseId={course.id}
                pipelines={course.crm_pipelines}
                onChange={(next) => setBlock(i, next)}
                onMove={(dir) => moveBlock(i, dir)}
                onDelete={() => delBlock(i)}
                onDuplicate={() => dupBlock(i)}
                drag={dragProps(i)} />
            ))}
          </div>
        )}
        <AddBlock onAdd={addBlock} />
      </section>

      {/* ── AD-17: SEO и превью ссылки ─────────────────────────────────────── */}
      <section className="acad-section">
        <div className="acad-section__head">
          <Icon name="link" size={15} /><span className="acad-section__title">SEO и превью ссылки</span>
        </div>
        <Field label="Заголовок страницы" hint={'пусто — название курса «' + (course.title || '') + '»'}>
          <TextInput value={state.meta.title}
            onChange={(e) => setMeta('title', e.target.value)} maxLength={200} />
        </Field>
        <Field label="Описание" hint="пусто — краткое описание курса; до 400 символов">
          <Textarea value={state.meta.description} minRows={2} maxLength={400}
            onChange={(e) => setMeta('description', e.target.value)} />
        </Field>
        <ImageField desc={{ label: 'Картинка превью (og:image)',
          hint: 'пусто — обложка курса · загрузите файл или вставьте ссылку http(s)://' }}
          value={state.meta.og_image} courseId={course.id}
          onChange={(v) => setMeta('og_image', v)} />
        {/* Честная граница: теги ставит клиент, а мессенджеры и поисковые роботы
            JS не исполняют — превью в чате отсюда НЕ появится. Обещать его в
            подсказке значило бы продать методисту то, чего нет. */}
        <div className="land-note">
          <Icon name="info" size={15} />
          <span className="t-caption">
            Эти поля видит вкладка браузера и наш публичный API страницы курса.
            Превью при отправке ссылки в мессенджер они пока не создают: разметку
            подставляет JavaScript, а мессенджеры и поисковые роботы его не
            выполняют — для превью нужна серверная отдача метатегов.
          </span>
        </div>
      </section>
    </div>
  );
}

export { LandingTab };
